/**
 * Economy system: gatherers fill their loads from wild tiles or from inside
 * farms, forestries and mines, and still units with a load unload it into an
 * adjacent own city. `gatherResource`/`gatherRate` are the single source of
 * truth the UI reads too.
 */
import { axialHexDistance } from '../../hexmap/coords';
import { getHexTileMapKey } from '../../hexmap/model/keys';
import { getTile } from '../../hexmap/model/map';
import type { HexTileMapKey } from '../../hexmap/model/types';
import { toMilli } from '../fixed';
import { buildingIndex, covers, growthBonus } from '../footprint';
import { buildingLevel, levelStat } from '../levels';
import { deposit, gatherDepositCity, holdsStock } from '../stock';
import { mintOrder, outsideTerritory, territoryOwners } from '../territory';
import type { Building, Cell, EntityId, SimContext, SimState, SystemFn, Unit } from '../types';

/**
 * Resource id the unit would gather this tick where it stands, or null.
 * Single source of truth for gather eligibility (the economy system and the
 * UI's "gathering" status both use it).
 *
 * Rules: only idle units with the `gather` action gather (a gather order
 * held where the unit stands counts as idle: `heldGather`), and only while
 * their load has room and holds the same resource (one resource at a time).
 * An own completed building that `provides` a resource (a farm) yields it
 * without depleting, to the workers inside it holding its `workerSlots`,
 * while it stands in its owner's territory; any other building (or being
 * inside one) blocks gathering;
 * otherwise the wild tile yields its resource while it has any left. Pass
 * `owners` (from `territoryOwners`) and `index` (`economyIndex`) when
 * calling for many units in one tick.
 */
function gatherResource(
  state: SimState,
  ctx: SimContext,
  unit: Unit,
  owners?: ReadonlyMap<HexTileMapKey, EntityId>,
  index?: EconomyIndex
): string | null {
  const { rules } = ctx;
  const unitDef = rules.units[unit.type];
  if (!unitDef?.actions.includes('gather')) return null;
  if ('path' in unit || (unit.order && !heldGather(unit))) return null;
  if (isFull(unit, unitDef.carryCapacity)) return null;

  const res = tileYield(state, ctx, unit, owners, index);
  if (!res || (unit.carry && unit.carry.res !== res)) return null;
  return res;
}

/**
 * Whether `unit` stands on the target of its gather order (a pinned worker,
 * see automation/worker.ts `stepAssigned`) and isn't hauling: it then works
 * like an idle unit. Inside a building its `pos` is the building's tile.
 */
function heldGather(unit: Unit): boolean {
  const o = unit.order;
  return (
    o?.kind === 'gather' &&
    o.pin === true &&
    !o.haul &&
    o.target.q === unit.pos.q &&
    o.target.r === unit.pos.r
  );
}

/** What the unit's tile yields it, ignoring its load (see `gatherResource`). */
function tileYield(
  state: SimState,
  ctx: SimContext,
  unit: Unit,
  owners?: ReadonlyMap<HexTileMapKey, EntityId>,
  index?: EconomyIndex
): string | null {
  // Inside an own completed farm or forestry: it works one of its slots.
  const b = providingBuilding(state, ctx, unit);
  if (b) {
    // Outside its owner's territory it's idle (see outsideTerritory).
    if (outsideTerritory(state, ctx.rules, b, owners)) return null;
    const slot = workersInside(state, ctx.rules, b, index).findIndex((u) => u.id === unit.id);
    const res = ctx.rules.buildings[b.type]?.provides?.resource ?? null;
    return slot >= 0 && slot < workerSlots(ctx.rules, b) ? res : null;
  }
  // Inside anything else, or on any tile of a building's footprint: nothing.
  if (unit.inside) return null;
  if (
    index
      ? index.covered.has(getHexTileMapKey(unit.pos))
      : Object.values(state.buildings).some((c) => covers(c, unit.pos))
  ) {
    return null;
  }

  const wild = tileResources(state, ctx, unit.pos);
  return wild && wild.remaining > 0 ? wild.resourceId : null;
}

/**
 * How many workers can work inside building `b`: its level's `workerSlots`
 * (1 unless set), plus `growth.slotsPerExtraTile` per extra footprint tile.
 */
function workerSlots(rules: SimContext['rules'], b: Building): number {
  const def = rules.buildings[b.type];
  const base = (def && levelStat(def, buildingLevel(b), 'workerSlots')) ?? 1;
  return base + growthBonus(rules, b, 'slotsPerExtraTile');
}

/**
 * The owner's gatherers inside building `b` (`Unit.inside`) with room in
 * their load and no order (bar a held gather order), oldest first. The first `workerSlots` of them
 * work it. `index` (`economyIndex`) skips the scan over every unit.
 */
function workersInside(
  state: SimState,
  rules: SimContext['rules'],
  b: Building,
  index?: EconomyIndex
): Unit[] {
  const candidates = index
    ? (index.inside.get(b.id) ?? [])
    : Object.values(state.units)
        .filter((u) => u.inside === b.id)
        .sort(byMintOrder);
  // Pinned workers holding their gather order first: their slots are theirs.
  const held = (u: Unit) => (heldGather(u) ? 0 : 1);
  return candidates
    .filter(
      (u) =>
        u.owner === b.owner &&
        !('path' in u) &&
        (!u.order || heldGather(u)) &&
        !!rules.units[u.type]?.actions.includes('gather') &&
        !isFull(u, rules.units[u.type]?.carryCapacity)
    )
    .sort((a, c) => held(a) - held(c));
}

function byMintOrder(a: Unit, c: Unit): number {
  return mintOrder(a.id) - mintOrder(c.id) || (a.id < c.id ? -1 : 1);
}

/**
 * What the economy looks up per unit, built once per pass: the units inside
 * each building (oldest first) and the building covering each tile. Valid
 * while no unit enters or leaves a building and no building changes, which
 * holds through the economy system; loads may change (`workersInside`
 * filters those, and orders, at call time).
 */
interface EconomyIndex {
  inside: Map<EntityId, Unit[]>;
  covered: Map<HexTileMapKey, Building>;
}

function economyIndex(state: SimState): EconomyIndex {
  const inside = new Map<EntityId, Unit[]>();
  for (const u of Object.values(state.units)) {
    if (!u.inside) continue;
    const list = inside.get(u.inside);
    if (list) list.push(u);
    else inside.set(u.inside, [u]);
  }
  for (const list of inside.values()) list.sort(byMintOrder);
  return { inside, covered: buildingIndex(state) };
}

/** Whether the unit's load is at its `carryCapacity` (never, without one). */
function isFull(u: Unit, capacity: number | undefined): boolean {
  return capacity !== undefined && !!u.carry && u.carry.amount >= toMilli(capacity);
}

/** Resource id a tile type yields (first-writer-wins over sorted ids), if any. */
function tileResourceId(rules: SimContext['rules'], tileType: string): string | null {
  for (const id of Object.keys(rules.resources).sort()) {
    if ((rules.resources[id]?.gatheredFrom as readonly string[] | undefined)?.includes(tileType)) {
      return id;
    }
  }
  return null;
}

/** A wild tile's resource; amounts in thousandths (fixed.ts). */
interface TileResources {
  resourceId: string;
  /** Amount the tile started with (its resourceValue × pacing.tileYield). */
  initial: number;
  /** Amount still gatherable (wild tiles deplete; farms don't). */
  remaining: number;
}

/** A tile's wild resource and how much of it is left, or null if it yields nothing. */
function tileResources(state: SimState, ctx: SimContext, pos: Cell): TileResources | null {
  const tile = getTile(ctx.terrain, pos);
  if (!tile) return null;
  const resourceId = tileResourceId(ctx.rules, tile.type);
  if (!resourceId) return null;
  const initial = toMilli(tile.resourceValue * ctx.rules.pacing.tileYield);
  const remaining = state.tileResourceOverride[getHexTileMapKey(pos)] ?? initial;
  return { resourceId, initial, remaining: Math.max(0, remaining) };
}

/** The own completed building that `provides` a resource (a farm) the unit is inside, if any. */
function providingBuilding(state: SimState, ctx: SimContext, unit: Unit): Building | undefined {
  const b = unit.inside ? state.buildings[unit.inside] : undefined;
  if (!b || b.owner !== unit.owner || b.buildProgress < 1) return undefined;
  return ctx.rules.buildings[b.type]?.provides ? b : undefined;
}

/**
 * Resources per second the unit gathers where it stands (0 if it isn't
 * gathering): `pacing.baseGatherRate`, times the providing building's rate at
 * its level on a farm, plus the `gatherBonus` of the own city whose territory
 * the tile is in, times the unit type's `gatherMultiplier` (a labourer's
 * tools). Pass `owners` (from `territoryOwners`) and `index`
 * (`economyIndex`) when calling for many units in one tick.
 */
function gatherRate(
  state: SimState,
  ctx: SimContext,
  unit: Unit,
  owners: ReadonlyMap<HexTileMapKey, EntityId> = territoryOwners(state, ctx.rules),
  index?: EconomyIndex
): number {
  if (!gatherResource(state, ctx, unit, owners, index)) return 0;
  let rate = ctx.rules.pacing.baseGatherRate * (ctx.rules.units[unit.type]?.gatherMultiplier ?? 1);
  const provider = providingBuilding(state, ctx, unit);
  const providerDef = provider && ctx.rules.buildings[provider.type];
  if (provider && providerDef) {
    rate *= levelStat(providerDef, buildingLevel(provider), 'rate') ?? 1;
  }
  const city = state.buildings[owners.get(getHexTileMapKey(unit.pos)) ?? ''];
  const cityDef = city && ctx.rules.buildings[city.type];
  if (city && cityDef && city.owner === unit.owner) {
    rate *= 1 + (levelStat(cityDef, buildingLevel(city), 'gatherBonus') ?? 0);
  }
  return rate;
}

/**
 * Gathering fills a gatherer's load (`carry`, up to its `carryCapacity`); it
 * reaches a city's stock when the gatherer, still, is next to an own completed
 * city or inside one (see `unloadCity`) and spends `pacing.depositTicks`
 * unloading there: while hauling (`auto.phase`, a pinned order's `haul`), or when it isn't gathering
 * (full, or nothing to gather where it is). A gatherer without a
 * `carryCapacity` deposits at once, into the own city whose territory holds
 * the tile, else the nearest (see `gatherDepositCity`); with no completed
 * city that gain is lost.
 */
const economy: SystemFn = (state: SimState, ctx: SimContext, _inputs, dt: number): void => {
  const owners = territoryOwners(state, ctx.rules);
  const index = economyIndex(state);
  for (const unit of Object.values(state.units)) {
    const player = state.players[unit.owner];
    if (!player) continue;
    const resourceId = gatherResource(state, ctx, unit, owners, index);
    if (unit.carry) {
      const hauling =
        unit.auto?.phase === 'haul' || (unit.order?.kind === 'gather' && unit.order.haul === true);
      const city = hauling || !resourceId ? unloadCity(state, ctx, unit) : undefined;
      if (city) {
        unload(state, ctx, unit, city);
        continue;
      }
      delete unit.carry.unloading;
    }
    if (!resourceId) continue;

    const rate = gatherRate(state, ctx, unit, owners, index);
    const wild = providingBuilding(state, ctx, unit) ? null : tileResources(state, ctx, unit.pos);
    // In thousandths (fixed.ts), like the load, the tile and the stock.
    const carryCapacity = ctx.rules.units[unit.type]?.carryCapacity;
    const capacity = carryCapacity === undefined ? undefined : toMilli(carryCapacity);
    const room =
      capacity === undefined ? Number.POSITIVE_INFINITY : capacity - (unit.carry?.amount ?? 0);
    // Farms never run out; wild tiles drain.
    const gain = Math.min(
      toMilli(rate * dt),
      room,
      wild ? wild.remaining : Number.POSITIVE_INFINITY
    );
    if (wild) state.tileResourceOverride[getHexTileMapKey(unit.pos)] = wild.remaining - gain;
    if (capacity === undefined) {
      const cityId = gatherDepositCity(state, ctx.rules, unit.owner, unit.pos, owners);
      if (cityId) deposit(state, cityId, resourceId, gain);
    } else if (gain > 0) {
      unit.carry = { res: resourceId, amount: (unit.carry?.amount ?? 0) + gain };
    }
  }
};

/** Unloads a tick of `unit`'s carry into `city`, depositing it after `pacing.depositTicks` ticks. */
function unload(state: SimState, ctx: SimContext, unit: Unit, city: Building): void {
  const carry = unit.carry;
  if (!carry) return;
  carry.unloading = (carry.unloading ?? 0) + 1;
  if (carry.unloading < ctx.rules.pacing.depositTicks) return;
  deposit(state, city.id, carry.res, carry.amount);
  delete unit.carry;
}

/**
 * The own completed city (a stock holder) `owner` can unload into from
 * `cell`: one on it or next to it (oldest first).
 */
function cityAt(state: SimState, ctx: SimContext, owner: string, cell: Cell): Building | undefined {
  let best: Building | undefined;
  for (const b of Object.values(state.buildings)) {
    if (b.owner !== owner || axialHexDistance(b.pos, cell) > 1) continue;
    if (!holdsStock(ctx.rules, b)) continue;
    if (!best || mintOrder(b.id) < mintOrder(best.id)) best = b;
  }
  return best;
}

/**
 * The city a still `unit` unloads into: the own city it's inside, else an
 * own city next to its tile (see `cityAt`). None while it moves or is inside
 * another building.
 */
function unloadCity(state: SimState, ctx: SimContext, unit: Unit): Building | undefined {
  if (unit.path) return undefined;
  if (unit.inside) {
    const b = state.buildings[unit.inside];
    return b && b.owner === unit.owner && holdsStock(ctx.rules, b) ? b : undefined;
  }
  return cityAt(state, ctx, unit.owner, unit.pos);
}

export type { TileResources };
export {
  cityAt,
  economy,
  gatherRate,
  gatherResource,
  tileResources,
  tileYield,
  unloadCity,
  workerSlots,
  workersInside,
};
